import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { homeCategories } from "@/mocks/categories";

const mainLinks = [
  { to: "/", label: "首页" },
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  const closeAll = () => {
    setMobileOpen(false);
    setCategoryOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-background-200" : "bg-background-50 border-transparent"
      }`}
    >
      <div className="flex items-center justify-between gap-4 h-16 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0" onClick={closeAll}>
          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-clapperboard-fill text-lg"></i>
          </span>
          <span className="text-lg font-heading font-bold tracking-tight text-foreground-950">
            天龙<span className="text-primary-600">影院</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {mainLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-label rounded-md transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCategoryOpen((v) => !v)}
              className="flex items-center gap-1 px-3 py-2 text-sm font-label rounded-md text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              分类
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? "rotate-180" : ""}`}></i>
              </span>
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full pt-2 w-64">
                <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2">
                  {homeCategories.map((cat) => (
                    <Link
                      key={cat.key}
                      to={`/category/${cat.key}`}
                      onClick={closeAll}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-label text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden lg:flex items-center">
            <div className="flex items-center gap-2 h-9 w-60 px-3 rounded-full border border-background-300 bg-background-100 focus-within:border-primary-400 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影 / 电视剧"
                className="w-full bg-transparent text-sm font-label text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
          </form>

          <Link
            to="/search"
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-700 hover:text-primary-600 hover:border-primary-300 transition-colors"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-5 pt-4">
          <form onSubmit={submitSearch} className="mb-4">
            <div className="flex items-center gap-2 h-10 px-3 rounded-full border border-background-300 bg-background-100 focus-within:border-primary-400 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影 / 电视剧"
                className="w-full bg-transparent text-sm font-label text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
          </form>
          <div className="flex flex-col gap-1">
            {mainLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={closeAll}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-label transition-colors ${
                    isActive ? "text-primary-600 bg-primary-50" : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-4">
            <p className="px-1 mb-2 text-xs font-label text-foreground-400">热门分类</p>
            <div className="flex flex-wrap gap-2">
              {homeCategories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  onClick={closeAll}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-sm font-label text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}